import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { DocumentUser, DocumentPage } from "@shared/schema";

interface ReadReceiptRow {
  user: DocumentUser;
  viewedPageIds: string[];
}

interface ReadReceiptsContentProps {
  documentId: string;
  versionId: string | undefined;
  versionNumber: number | undefined;
  documentColor: string;
}

export interface ReadReceiptsPanelProps extends ReadReceiptsContentProps {
  open: boolean;
  onClose: () => void;
}

function initials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Inner content — only mounts when the panel is open, so gcTime:0 ensures
 * fresh fetches on every open without any extra state or effects.
 */
function ReadReceiptsContent({
  documentId,
  versionId,
  versionNumber,
  documentColor,
}: ReadReceiptsContentProps) {
  const { data: pagesResponse, isLoading: isLoadingPages } = useQuery({
    queryKey: ["/api/documents", documentId, "versions", versionNumber?.toString(), "pages"],
    enabled: !!versionNumber,
    staleTime: 0,
    gcTime: 0,
  });

  const { data: receipts = [], isLoading: isLoadingReceipts } = useQuery<ReadReceiptRow[]>({
    queryKey: ["/api/documents", documentId, "versions", versionId, "read-receipts"],
    enabled: !!versionId,
    staleTime: 0,
    gcTime: 0,
  });

  const allPages: DocumentPage[] = (pagesResponse as any)?.pages ?? [];
  const visiblePages = allPages.filter((p) => !p.hidden);
  const totalPages = visiblePages.length;
  const isLoading = isLoadingPages || isLoadingReceipts;

  const containerPx = 220;
  const gapPx = 2;
  const totalGap = Math.max(0, totalPages - 1) * gapPx;
  const rawDot = totalPages > 0 ? Math.floor((containerPx - totalGap) / totalPages) : 10;
  const dotPx = Math.max(4, Math.min(10, rawDot));

  return (
    <>
      <div className="px-3 py-2 border-b border-border">
        <p className="text-sm font-medium text-foreground">
          Read receipts{versionNumber !== undefined ? ` — v${versionNumber}` : ""}
        </p>
        {totalPages > 0 && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalPages} page{totalPages !== 1 ? "s" : ""} in this version
          </p>
        )}
      </div>

      <div className="max-h-72 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-xs text-muted-foreground animate-pulse">Loading…</div>
          </div>
        ) : receipts.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <p className="text-xs text-muted-foreground">No participants yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {receipts.map(({ user, viewedPageIds }) => {
              const viewedSet = new Set(viewedPageIds);
              const viewedCount = visiblePages.filter((p) => viewedSet.has(p.id)).length;

              return (
                <li key={user.id} className="flex items-center gap-3 px-3 py-2">
                  <Avatar className="w-6 h-6 flex-shrink-0">
                    <AvatarFallback className="text-xs bg-muted">
                      {initials(user.name || "?")}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <span className="text-xs font-medium text-foreground truncate">
                      {user.name || "Unknown"}
                    </span>

                    {totalPages > 0 && (
                      <TooltipProvider delayDuration={150}>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex items-center flex-shrink-0"
                            style={{ gap: `${gapPx}px` }}
                          >
                            {visiblePages.map((page, idx) => {
                              const seen = viewedSet.has(page.id);
                              return (
                                <Tooltip key={page.id}>
                                  <TooltipTrigger asChild>
                                    <span
                                      style={{
                                        width: dotPx,
                                        height: dotPx,
                                        minWidth: dotPx,
                                        borderRadius: "50%",
                                        backgroundColor: seen
                                          ? documentColor
                                          : "rgba(128, 128, 128, 0.35)",
                                        border: "none",
                                        flexShrink: 0,
                                        display: "inline-block",
                                        cursor: "default",
                                      }}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs px-2 py-1">
                                    {seen
                                      ? `Page ${idx + 1} — viewed`
                                      : `Page ${idx + 1} — not yet viewed`}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {viewedCount}/{totalPages}
                          </span>
                        </div>
                      </TooltipProvider>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/**
 * ReadReceiptsPanel — custom fixed-position panel, no Radix Popover.
 * Controlled by `open` / `onClose` from the parent. Only mounts
 * ReadReceiptsContent when open so queries always fire fresh.
 */
export function ReadReceiptsPanel({
  open,
  onClose,
  documentId,
  versionId,
  versionNumber,
  documentColor,
}: ReadReceiptsPanelProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: PointerEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    // Defer listener attachment so the pointer-down that opened the panel
    // doesn't immediately re-fire the close handler.
    const timer = setTimeout(() => {
      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="fixed right-4 top-[52px] z-50 w-80 rounded-md bg-popover border border-border shadow-md overflow-hidden"
      data-testid="read-receipts-panel"
    >
      <ReadReceiptsContent
        documentId={documentId}
        versionId={versionId}
        versionNumber={versionNumber}
        documentColor={documentColor}
      />
    </div>
  );
}
